import React from "react";
import { Container, Typography, Box, Paper, Divider } from "@mui/material";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../GASetup/AnalyticsTracker";
import {
  cardOutline,
  fonts,
  headingFonts,
  semiBoldFonts,
} from "../../Styles/Styles";

const TermsOfService = () => {
  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 5,
        mb: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Helmet>
        <title>Terms of Service - Listed.ky</title>
        <meta name="description" content="Terms of Service - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />

      {/* Page Title */}
      <Typography
        variant="h3"
        sx={{
          mb: 2,
          ...semiBoldFonts,
          letterSpacing: 2,
          color: "var(--primary-color)",
          textTransform: "uppercase",
          position: "relative",
          display: "inline-block",
          paddingBottom: "8px",
          "&::after": {
            content: '""',
            width: "80px",
            height: "4px",
            backgroundColor: "var(--primary-color)",
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
          },
        }}
      >
        Terms of Service
      </Typography>

      {/* Terms of Service Card */}
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          borderRadius: "12px",
          backgroundColor: "white",
          ...cardOutline,
        }}
      >
        {[
          {
            title: "1. Acceptance of Terms",
            content:
              "By creating an account on Listed.ky, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. You must check the box indicating your acceptance of these terms during the account creation process. Anything posted on this page is considered live, active, and enforced.",
          },
          {
            title: "2. Account Creation",
            content:
              "Accounts may only be created by individuals with a verified link to the Cayman Islands, such as a local phone number. For accounts created outside of the Cayman Islands, SMS verification or a locally issued government ID will be required, at the discretion of the company. We reserve the right to close, bar, or limit account creation at any time.",
          },
          {
            title: "3. Vehicle Listing Limitations",
            content:
              "The number of vehicle ads for sale is limited to three (3) per account, applicable to both personal and business accounts. If a business would like to list multiple vehicles, they must have a paid account.",
          },
          {
            title: "4. Verified Sellers",
            content:
              "To become a verified seller, you must complete the following steps:",
            list: [
              "Submit a local government ID.",
              "Verify your email address OR verify your phone number via SMS (must be a local number).",
              "Verified sellers will display a verified badge on their listings.",
            ],
          },
          {
            title: "5. Prohibited Conduct",
            content:
              "Users are expected to engage respectfully with other members of the community. The following actions are strictly prohibited:",
            list: [
              "Harassment of sellers or buyers.",
              "Bypassing these Terms of Service in any manner.",
            ],
          },
          {
            title: "6. Reporting Ads",
            content: "Users may report ads for the following reasons:",
            list: ["Fraud", "Fake", "Phishing", "Harassment"],
          },
          {
            title: "7. Reporting and Enforcement",
            content:
              "If a user is reported for harassment or abuse of service, we will investigate the claim. If the claim is found to be true, the user will receive one warning. In cases of extreme harassment or abuse, we reserve the right to close the offending account without prior communication.",
          },
          {
            title: "8. Communication Review",
            content:
              "We reserve the right to review communication between buyers and sellers that takes place on the platform. This is done solely to investigate claims or reports.",
          },
          {
            title: "9. Disclaimer of Responsibility",
            content:
              "We are not responsible for any failure to deliver or response from sellers or buyers.",
          },
          {
            title: "10. Changes to Terms",
            content:
              "We reserve the right to modify these Terms of Service at any time. Any changes will be effective immediately upon posting on this page. Continued use of Listed.ky after such changes constitutes your acceptance of the new Terms of Service.",
          },
        ].map((section, index) => (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                ...headingFonts,
                color: "var(--primary-color)",
              }}
            >
              {section.title}
            </Typography>
            <Typography
              paragraph
              sx={{
                ...fonts,
                color: "text.secondary",
                mt: 1,
              }}
            >
              {section.content}
            </Typography>
            {section.list && (
              <ul>
                {section.list.map((item, idx) => (
                  <li key={idx} style={{ ...fonts, color: "rgb(0 0 0 / 60%)" }}>
                    {item}
                  </li>
                ))}
              </ul>
            )}
            {index !== 9 && (
              <Divider sx={{ my: 3, borderColor: "rgba(0, 0, 0, 0.1)" }} />
            )}
          </Box>
        ))}
      </Paper>
    </Container>
  );
};

export default TermsOfService;
